import { Briefcase, CalendarCheck, ClipboardList, FileSignature, Map, Megaphone, MessageSquare, Package, Receipt, ShieldCheck, Users, BarChart3 } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";
import { findProItem } from "@/components/layout/nav";
import { DemoBadge } from "@/components/common/DemoBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { ModuleCard } from "@/components/common/ModuleCard";
import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";
import { ProBankCustomers, ProBilling, ProClaims, ProContracts, ProOverview, ProRegistrations, ProRepairs } from "@/components/pro/ProSections";
import { ProCampaigns } from "@/components/pro/ProCampaigns";

const SECTIONS = {
  repairer: { demandes: () => <ProRepairs />, "rendez-vous": () => <ProRepairs statuses={["accepted", "in_progress"]} empty="Aucune intervention planifiée." />, facturation: () => <ProBilling /> },
  manufacturer: { catalogue: () => <ProRegistrations view="models" />, sav: () => <ProRepairs empty="Aucune demande SAV." />, parc: () => <ProRegistrations />, campagnes: () => <ProCampaigns /> },
  insurer: { assures: () => <ProContracts />, sinistres: () => <ProClaims />, statistiques: () => <ProOverview /> },
  bank: { licences: () => <ProBankCustomers /> },
};

const FEATURES = {
  repairer: [
    { icon: ClipboardList, title: "Demandes & devis", text: "Recevez les demandes qualifiées et envoyez vos devis." },
    { icon: CalendarCheck, title: "Rendez-vous & interventions", text: "Planning, suivi et compte rendu d'intervention." },
    { icon: Receipt, title: "Facturation & abonnement", text: "Plans professionnels configurables." },
  ],
  manufacturer: [
    { icon: Package, title: "Catalogue & pièces", text: "Modèles, notices, pièces détachées, rappels produits." },
    { icon: MessageSquare, title: "SAV multilingue", text: "Tickets et messagerie avec traduction." },
    { icon: Map, title: "Parc installé", text: "Produits enregistrés par leurs propriétaires (modèle, n° de série, garantie), sans aucune donnée d'identité." },
    { icon: Megaphone, title: "Campagnes & reprises", text: "Campagnes in-app au contact qualifié, avec consentement." },
  ],
  insurer: [
    { icon: Users, title: "Assurés actifs", text: "Licences et comptage des assurés actifs facturables." },
    { icon: FileSignature, title: "Sinistres autorisés", text: "Accès uniquement aux dossiers explicitement partagés." },
    { icon: BarChart3, title: "Statistiques agrégées", text: "Tableaux de bord sans données personnelles." },
  ],
  bank: [
    { icon: ShieldCheck, title: "Clients reliés", text: "Clients dont les comptes sont agrégés dans CASHMY, encours et nombre de comptes." },
    { icon: FileSignature, title: "Dossiers partagés", text: "Dossiers financiers volontairement partagés et révocables." },
    { icon: CalendarCheck, title: "Rendez-vous", text: "Prise de rendez-vous avec les conseillers." },
  ],
};

export default function ProPortal() {
  const { user } = useAuth();
  const { section } = useParams();
  if (!user.organizations.length) return <EmptyState icon={Briefcase} title="Aucune organisation" text="Votre compte n'est rattaché à aucune organisation professionnelle." />;
  if (section) {
    const nav = findProItem(user, section);
    const f = nav && FEATURES[user.organizations[0].type]?.[nav.feature];
    if (!f) return <Navigate to="/pro" replace />;
    const Live = SECTIONS[user.organizations[0].type]?.[section];
    return (
      <div className="space-y-8" data-testid={`pro-section-${section}`}>
        <PageHeader eyebrow={user.organizations[0].name} title={nav.label} text={f.text} />
        {Live ? <Live /> : <EmptyState icon={nav.icon} title="Module en préparation" text="Cet espace sera développé avec la Partie 6/8 (portails professionnels)." />}
      </div>
    );
  }
  return (
    <div className="space-y-10" data-testid="pro-portal-page">
      <PageHeader eyebrow="Portails professionnels" title={`Espace ${t(`orgTypes.${user.organizations[0].type}`)}`} text="Les professionnels accèdent uniquement aux données strictement nécessaires à leur mission." />
      {user.organizations.map((o) => (
        <section key={o.id} className="space-y-4" data-testid={`org-section-${o.id}`}>
          <div className="hero-navy flex flex-wrap items-center gap-3 rounded-2xl p-6">
            <Briefcase className="h-5 w-5 text-mint" />
            <div className="flex-1">
              <div className="font-heading text-xl font-bold">{o.name}</div>
              <div className="text-sm text-[#F8FAF8]/70">{t(`orgTypes.${o.type}`)} · {o.role === "owner" ? "Responsable" : "Agent"}</div>
            </div>
            {o.is_demo && <DemoBadge className="border-gold bg-gold/25 !text-gold" />}
          </div>
          <ProOverview />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(FEATURES[o.type] || []).map((f, i) => <ModuleCard key={f.title} {...f} brief={6} delay={i * 50} testid={`org-feature-${o.type}-${i}`} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
